﻿import { MILL_1 } from "@/setup/mill-config/mill-config";
import type { DateOnly } from "@/shared/components/date-picker";
import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import {
	CampaignEventData,
	RollChangeStats,
} from "../models/CampaignEventData";
import {
	CreateSnapshotResult,
	DraftSnapshot,
	Event,
	EventsToCompareResult,
	EventToCompare,
	ProductionEventActual,
	RestoreSnapshotResult,
} from "../models/Event";

const moment = getMomentTz();

const resolveInlineProductionEventField = (field: keyof Event): string => {
	switch (field) {
		case "EventComments":
			return "Comments";
		case "TonsPerHour":
		case "TargetTPH":
			throw new Error(
				`Inline schedule editing must persist TargetTPHPercentage, not ${field}.`,
			);
		default:
			return field;
	}
};

const shouldSendAutocalculatedValue = (
	value: number | null | undefined,
): value is number => value != null && Number.isFinite(value);

export const getEvents = async (
	params: {
		start?: Date;
		end?: Date;
		eventId?: number;
		monthStart?: Date | null;
		dayBreakdown?: boolean;
		isDraftMode?: boolean;
		defaultAreaId?: string | number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Start", value: params.start?.toISOString() ?? null },
		{ name: "End", value: params.end?.toISOString() ?? null },
		{ name: "EventId", value: params.eventId ?? null },
		{
			name: "MonthStart",
			value: params.monthStart
				? moment(params.monthStart).format("YYYY-MM-DD")
				: null,
		},
		{ name: "DailyBreakdown", value: params.dayBreakdown || false },
		{ name: "DraftMode", value: params.isDraftMode || false },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetEvents]" : "[M2].[GetEvents]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((e: Event) => ({
			...e,
			Start: moment.utc(e["Start"]).toDate(),
			End: moment.utc(e["End"]).toDate(),
			CreatedAt: moment.utc(e["CreatedAt"]).toDate(),
			Deadline: e.Deadline ? moment.utc(e["Deadline"]).toDate() : null,
			InquiryCloseDate: e.InquiryCloseDate
				? moment.utc(e["InquiryCloseDate"]).toDate()
				: null,
			UpdatedAt: e.UpdatedAt ? moment.utc(e["UpdatedAt"]).toDate() : null,
			ScheduledMillYield: e.ScheduledMillYield ?? 0,
			ScheduledFinishedYield: e.ScheduledFinishedYield ?? 0,
			ActualMillYield: e.ActualMillYield ?? 0,
			ActualFinishedYield: e.ActualFinishedYield ?? 0,
		}));

		rows.sort((a: Event, b: Event) => a.Start.getTime() - b.Start.getTime());

		const lastMatchedId =
			rows.filter((e: Event) => e.IsMatched).at(-1)?.EventId ?? null;

		rows = rows.map((e: Event, index: number) => ({
			...e,
			IsLastMatched: e.IsMatched && e.EventId === lastMatchedId,
			PreviousSection: index > 0 ? rows[index - 1].SectionName : null,
			PreviousEnd: index > 0 ? rows[index - 1].End : null,
			NextStart: index < rows.length - 1 ? rows[index + 1].Start : null,
			NextSection: index < rows.length - 1 ? rows[index + 1].SectionName : null,
		}));

		return rows as Event[];
	} else {
		throw new Error(resp.message || "Error fetching events");
	}
};

export const getCampaignsByEventId = async (
	params: {
		eventId: number;
		isDraftMode: boolean;
		defaultAreaId?: string | number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: params.eventId },
		{ name: "DraftMode", value: params.isDraftMode },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetCampaignsByEventId]" : "[M2].[GetCampaignsByEventId]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		const campaigns = (
			get(resp, "data.tables[0].rows", []) as CampaignEventData[]
		).map((c) => ({
			...c,
			MonthStart: c.MonthStart
				? moment.utc(c.MonthStart as unknown as string).toDate()
				: null,
			CampaignStart: c.CampaignStart
				? moment.utc(c.CampaignStart as unknown as string).toDate()
				: null,
		}));
		const statsRows = get(resp, "data.tables[1].rows", []) as RollChangeStats[];
		const rollChangeStats: RollChangeStats | null = statsRows[0] ?? null;
		const actuals = (
			get(resp, "data.tables[2].rows", []) as ProductionEventActual[]
		).map((a) => ({
			...a,
			MonthStart: moment.utc(a.MonthStart as unknown as string).toDate(),
		}));

		return { campaigns, rollChangeStats, actuals };
	} else {
		throw new Error(resp.message || "Error fetching campaign data");
	}
};

export const getPendingConfirmEvents = async (
	params: {
		monthEnd: Date | null;
		defaultAreaId?: string | number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "MonthEnd", value: params.monthEnd },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetPendingConfirmEvents]" : "[M2].[GetPendingConfirmEvents]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((e: Event) => ({
			...e,
			Start: moment.utc(e["Start"]).toDate(),
			End: moment.utc(e["End"]).toDate(),
			CreatedAt: moment.utc(e["CreatedAt"]).toDate(),
			Deadline: e.Deadline ? moment.utc(e["Deadline"]).toDate() : null,
			InquiryCloseDate: e.InquiryCloseDate
				? moment.utc(e["InquiryCloseDate"]).toDate()
				: null,
			UpdatedAt: e.UpdatedAt ? moment.utc(e["UpdatedAt"]).toDate() : null,
			ScheduledMillYield: e.ScheduledMillYield ?? 0,
			ScheduledFinishedYield: e.ScheduledFinishedYield ?? 0,
		}));

		rows.sort((a: Event, b: Event) => a.Start.getTime() - b.Start.getTime());
		rows = rows.map((e: Event, index: number) => ({
			...e,
			PreviousSection: index > 0 ? rows[index - 1].SectionName : null,
			PreviousEnd: index > 0 ? rows[index - 1].End : null,
			NextStart: index < rows.length - 1 ? rows[index + 1].Start : null,
			NextSection: index < rows.length - 1 ? rows[index + 1].SectionName : null,
		}));

		return rows as Event[];
	} else {
		throw new Error(resp.message || "Error fetching pending confirm events");
	}
};

export const upsertEvent = async ({
	eventId,
	start,
	end,
	eventTypeCode,
	comments,
	isDraftMode,
	defaultAreaId,
}: {
	eventId: number | null;
	start: Date;
	end: Date | null;
	eventTypeCode: string;
	comments: string | null;
	isDraftMode: boolean;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "Start", value: start.toISOString() },
		{ name: "End", value: end?.toISOString() ?? null },
		{ name: "EventTypeCode", value: eventTypeCode },
		{ name: "Comments", value: comments },
	];

	const spName = isDraftMode
		? `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertDraftEvent]" : "[M2].[UpsertDraftEvent]"}`
		: `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertEvent]" : "[M2].[UpsertEvent]"}`;

	const resp = await apiService.callV2(spName, parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert event");
	}
};

export const upsertProductionEvent = async ({
	eventId,
	start,
	end,
	sectionId,
	statusCode,
	inquiryStatusCode,
	billetsStatusCode,
	deadline,
	inquiryCloseDate,
	targetTPH,
	targetTPHPercentage,
	updateSectionDefaults = false,
	scheduledChargedTons,
	scheduledRolledTons,
	scheduledFinishedTons,
	budgetTPH,
	scheduledRollingHours,
	scheduledRollChangeHours,
	scheduledMillYield,
	scheduledFinishedYield,
	actualChargedTons,
	actualRolledTons,
	actualFinishedTons,
	actualTPH,
	actualRollingHours,
	actualRollChangeHours,
	actualMillYield,
	actualFinishedYield,
	comments,
	isDraftMode,
	defaultAreaId,
}: {
	eventId: number | null;
	start: Date;
	end: Date | null;
	sectionId: number | null;
	statusCode?: string | null;
	inquiryStatusCode?: string | null;
	billetsStatusCode?: string | null;
	deadline?: Date | null;
	inquiryCloseDate?: Date | null;
	targetTPH: number | null;
	targetTPHPercentage: number | null;
	updateSectionDefaults?: boolean;
	scheduledChargedTons: number | null;
	scheduledRolledTons: number | null;
	scheduledFinishedTons: number | null;
	budgetTPH: number | null;
	scheduledRollingHours: number | null;
	scheduledRollChangeHours: number | null;
	scheduledMillYield: number | null;
	scheduledFinishedYield: number | null;
	actualChargedTons: number | null;
	actualRolledTons: number | null;
	actualFinishedTons: number | null;
	actualTPH: number | null;
	actualRollingHours: number | null;
	actualRollChangeHours: number | null;
	actualMillYield: number | null;
	actualFinishedYield: number | null;
	comments: string | null;
	isDraftMode: boolean;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "Start", value: start.toISOString() },
		{ name: "End", value: end?.toISOString() ?? null },
		{ name: "SectionId", value: sectionId },
		{ name: "StatusCode", value: statusCode ?? "P" },
		{ name: "InquiryStatusCode", value: inquiryStatusCode ?? "C" },
		{ name: "BilletsStatusCode", value: billetsStatusCode ?? "H" },
		{ name: "TargetTPH", value: targetTPH },
		{ name: "TargetTPHPercentage", value: targetTPHPercentage },
		{ name: "ScheduledChargedTons", value: scheduledChargedTons },
		{ name: "ScheduledRolledTons", value: scheduledRolledTons },
		{ name: "ScheduledFinishedTons", value: scheduledFinishedTons },
		{ name: "BudgetTPH", value: budgetTPH },
		{
			name: "ScheduledRollingHours",
			value: scheduledRollingHours,
		},
		{ name: "ScheduledRollChangeHours", value: scheduledRollChangeHours },
		{ name: "ScheduledMillYield", value: scheduledMillYield },
		{ name: "ScheduledFinishedYield", value: scheduledFinishedYield },
		// Actual* only sent to UpsertProductionEvent (confirmed events → SubEvents).
		// UpsertDraftProductionEvent has no Actual* columns; params are omitted in draft mode.
		...(!isDraftMode
			? [
					{ name: "ActualChargedTons", value: actualChargedTons },
					{ name: "ActualRolledTons", value: actualRolledTons },
					{ name: "ActualFinishedTons", value: actualFinishedTons },
					{ name: "ActualTPH", value: actualTPH },
					{ name: "ActualRollingHours", value: actualRollingHours },
					{ name: "ActualRollChangeHours", value: actualRollChangeHours },
					{ name: "ActualMillYield", value: actualMillYield },
					{ name: "ActualFinishedYield", value: actualFinishedYield },
				]
			: []),
		{ name: "Comments", value: comments },
	];

	if (deadline) {
		parameters.push({ name: "Deadline", value: deadline.toISOString() });
	}

	if (inquiryCloseDate) {
		parameters.push({
			name: "InquiryCloseDate",
			value: inquiryCloseDate.toISOString(),
		});
	}

	if (updateSectionDefaults) {
		parameters.push({
			name: "UpdateSectionDefaults",
			value: updateSectionDefaults,
		});
	}

	// Call the appropriate SP based on draft mode
	const spName = isDraftMode
		? `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertDraftProductionEvent]" : "[M2].[UpsertDraftProductionEvent]"}`
		: `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertProductionEvent]" : "[M2].[UpsertProductionEvent]"}`;

	const resp = await apiService.callV2(spName, parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert production event");
	}
};

export const upsertProductionEventActuals = async ({
	eventId,
	monthStart,
	actualChargedTons,
	actualRolledTons,
	actualFinishedTons,
	actualTPH,
	actualRollingHours,
	actualRollChangeHours,
	actualMillYield,
	actualFinishedYield,
	defaultAreaId,
}: {
	eventId: number;
	monthStart: Date;
	actualChargedTons: number | null;
	actualRolledTons: number | null;
	actualFinishedTons: number | null;
	actualTPH: number | null;
	actualRollingHours: number | null;
	actualRollChangeHours: number | null;
	actualMillYield: number | null;
	actualFinishedYield: number | null;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "MonthStart", value: moment.utc(monthStart).format("YYYY-MM-DD") },
		{ name: "ActualChargedTons", value: actualChargedTons },
		{ name: "ActualRolledTons", value: actualRolledTons },
		{ name: "ActualFinishedTons", value: actualFinishedTons },
		{ name: "ActualTPH", value: actualTPH },
		{ name: "ActualRollingHours", value: actualRollingHours },
		{ name: "ActualRollChangeHours", value: actualRollChangeHours },
		{ name: "ActualMillYield", value: actualMillYield },
		{ name: "ActualFinishedYield", value: actualFinishedYield },
	];

	const spName =
		Number(defaultAreaId) === MILL_1.id
			? "[M1].[UpsertProductionEvent]"
			: "[M2].[UpsertProductionEvent]";

	const resp = await apiService.callV2(spName, parameters);

	if (!resp.ok) {
		throw new Error(
			resp.message || "Error when upserting production event actuals",
		);
	}
};

export const upsertFieldProductionEvent = async ({
	eventId,
	start,
	end,
	sectionId,
	monthStart,
	field,
	value,
	autocalculatedFields,
	isDraftMode,
	defaultAreaId,
}: {
	eventId: number | null;
	start?: Date;
	end?: Date | null;
	sectionId?: number | null;
	monthStart?: Date | string | null;
	field: keyof Event;
	value: string | number | null;
	autocalculatedFields?: {
		status: "Scheduled" | "Actual";
		newRollingHs?: number | null;
		newMillYield: number | null;
		newFinishedYield: number | null;
	};
	isDraftMode: boolean;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{
			name: "MonthStart",
			value:
				typeof monthStart === "string"
					? monthStart
					: monthStart
						? moment(monthStart).format("YYYY-MM-DD")
						: null,
		},
		{ name: resolveInlineProductionEventField(field), value },
	];

	if (start) {
		parameters.push({ name: "Start", value: start.toISOString() });
	}
	if (end) {
		parameters.push({ name: "End", value: end.toISOString() });
	}
	if (sectionId) {
		parameters.push({ name: "SectionId", value: sectionId });
	}

	if (autocalculatedFields) {
		if (shouldSendAutocalculatedValue(autocalculatedFields.newRollingHs)) {
			parameters.push({
				name: `${autocalculatedFields.status}RollingHours`,
				value: autocalculatedFields.newRollingHs,
			});
		}
		if (shouldSendAutocalculatedValue(autocalculatedFields.newMillYield)) {
			parameters.push({
				name: `${autocalculatedFields.status}MillYield`,
				value: autocalculatedFields.newMillYield,
			});
		}
		if (shouldSendAutocalculatedValue(autocalculatedFields.newFinishedYield)) {
			parameters.push({
				name: `${autocalculatedFields.status}FinishedYield`,
				value: autocalculatedFields.newFinishedYield,
			});
		}
	}

	// Call the appropriate SP based on draft mode
	const spName = isDraftMode
		? `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertDraftProductionEvent]" : "[M2].[UpsertDraftProductionEvent]"}`
		: `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertProductionEvent]" : "[M2].[UpsertProductionEvent]"}`;

	const resp = await apiService.callV2(spName, parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert production event");
	}
};

export const deleteEvent = async ({
	eventId,
	deleteMode,
	defaultAreaId,
}: {
	eventId: number;
	deleteMode: "Main" | "Draft" | "Both";
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "DeleteMode", value: deleteMode },
	];

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[DeleteEvent]" : "[M2].[DeleteEvent]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when delete event");
	}
};

export const updateEventDates = async ({
	eventId,
	newStartDate,
	newEndDate,
	isDraftMode,
	defaultAreaId,
}: {
	eventId: number;
	newStartDate: Date;
	newEndDate: Date;
	isDraftMode: boolean;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EventId", value: eventId },
		{ name: "Start", value: newStartDate.toISOString() },
		{ name: "End", value: newEndDate.toISOString() },
	];

	// Call the appropriate SP based on draft mode
	const spName = isDraftMode
		? `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertDraftEvent]" : "[M2].[UpsertDraftEvent]"}`
		: `${Number(defaultAreaId) === MILL_1.id ? "[M1].[UpsertEvent]" : "[M2].[UpsertEvent]"}`;

	const resp = await apiService.callV2(spName, parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert date of event");
	}
};

// ============================================
// Draft/Confirm Sync Operations
// ============================================

export const resetDraft = async (defaultAreaId?: string | number | null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[ResetDraft]" : "[M2].[ResetDraft]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when resetting draft");
	}

	const result = get(resp, "data.tables[0].rows[0]", null);
	return result;
};

export const confirmEvents = async (
	defaultAreaId?: string | number | null,
	confirmUntilEventId?: number | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (confirmUntilEventId) {
		parameters.push({
			name: "ConfirmUntilEventId",
			value: confirmUntilEventId,
		});
	}

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[ConfirmEvents]" : "[M2].[ConfirmEvents]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when confirming events");
	}

	const result = get(resp, "data.tables[0].rows[0]", null);
	return result;
};

export const getEventsToCompare = async (
	params: { defaultAreaId?: string | number | null },
	signal?: AbortSignal,
): Promise<EventsToCompareResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetEventsToCompare]" : "[M2].[GetEventsToCompare]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		// First result set: Main events
		let mainRows = get(resp, "data.tables[0].rows", []);
		// Second result set: Draft events
		let draftRows = get(resp, "data.tables[1].rows", []);

		const mapEvent = (e: EventToCompare): EventToCompare => ({
			...e,
			Start: moment.utc(e["Start"]).toDate(),
			End: e.End ? moment.utc(e["End"]).toDate() : null,
			CreatedAt: moment.utc(e["CreatedAt"]).toDate(),
			Deadline: e.Deadline ? moment.utc(e["Deadline"]).toDate() : null,
			InquiryCloseDate: e.InquiryCloseDate
				? moment.utc(e["InquiryCloseDate"]).toDate()
				: null,
			UpdatedAt: e.UpdatedAt ? moment.utc(e["UpdatedAt"]).toDate() : null,
			ScheduledMillYield: e.ScheduledMillYield ?? 0,
			ScheduledFinishedYield: e.ScheduledFinishedYield ?? 0,
			ActualMillYield: e.ActualMillYield ?? 0,
			ActualFinishedYield: e.ActualFinishedYield ?? 0,
		});

		mainRows = mainRows.map(mapEvent);
		draftRows = draftRows.map(mapEvent);

		return {
			mainEvents: mainRows as EventToCompare[],
			draftEvents: draftRows as EventToCompare[],
		};
	} else {
		throw new Error(resp.message || "Error fetching events to compare");
	}
};

// ============================================
// Draft Snapshot Operations
// ============================================

export const getDraftSnapshots = async (
	params: { defaultAreaId?: string | number | null },
	signal?: AbortSignal,
): Promise<DraftSnapshot[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetDraftSnapshots]" : "[M2].[GetDraftSnapshots]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []);

		rows = rows.map((s: DraftSnapshot) => ({
			...s,
			CreatedAt: moment.utc(s["CreatedAt"]).toDate(),
			FirstEventStart: s.FirstEventStart
				? moment.utc(s["FirstEventStart"]).toDate()
				: null,
			LastEventEnd: s.LastEventEnd
				? moment.utc(s["LastEventEnd"]).toDate()
				: null,
		}));

		return rows as DraftSnapshot[];
	} else {
		throw new Error(resp.message || "Error fetching draft snapshots");
	}
};

export const createDraftSnapshot = async (
	defaultAreaId?: string | number | null,
	reason: "MANUAL" | "RESET" | "RESTORE" = "MANUAL",
	description?: string,
): Promise<CreateSnapshotResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "Reason", value: reason },
		{ name: "Description", value: description ?? null },
	];

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[CreateDraftSnapshot]" : "[M2].[CreateDraftSnapshot]"}`,
		parameters,
	);

	if (resp.ok) {
		const result = get(resp, "data.tables[0].rows[0]", null);
		return result as CreateSnapshotResult;
	} else {
		throw new Error(resp.message || "Error creating draft snapshot");
	}
};

export const restoreFromSnapshot = async (
	snapshotId: number,
	defaultAreaId?: string | number | null,
): Promise<RestoreSnapshotResult> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "SnapshotId", value: snapshotId }];

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[RestoreFromSnapshot]" : "[M2].[RestoreFromSnapshot]"}`,
		parameters,
	);

	if (resp.ok) {
		const result = get(resp, "data.tables[0].rows[0]", null);
		return result as RestoreSnapshotResult;
	} else {
		throw new Error(resp.message || "Error restoring from snapshot");
	}
};

export const getEmailTopicName = (
	resource: string,
	defaultAreaId?: string | number | null,
) => `${Number(defaultAreaId) === MILL_1.id ? "M1" : "M2"}_${resource}`;

export const getEmailRecipientsResources = async (
	params: {
		resource: string;
		defaultAreaId?: string | number | null;
	},
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{
			name: "EmailTopicName",
			value: getEmailTopicName(params.resource, params.defaultAreaId),
		},
	];

	const resp = await apiService.callV2(
		"[SYSTEM].[GetEmailAddressesByTopic]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as { Address: string }[];

		return rows
			.map((row) => row.Address?.trim())
			.filter((address) => !!address && address.length > 0);
	} else {
		throw new Error(resp.message || "Error fetching email recipients");
	}
};

export const getEmailAddresses = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();

	const resp = await apiService.callV2(
		"[SYSTEM].[GetEmailAddresses]",
		[],
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as { Address: string }[];

		return rows
			.map((row) => row.Address?.trim())
			.filter((address) => !!address && address.length > 0);
	} else {
		throw new Error(resp.message || "Error fetching email addresses");
	}
};

export const sendCalendarPublishNotification = async ({
	resource,
	emailRecipients,
	eventsPublished = 0,
	publisherNote,
	fromDate,
	toDate,
	addRecipientsToTopic = false,
	defaultAreaId,
}: {
	resource: string;
	emailRecipients: string;
	eventsPublished?: number;
	publisherNote?: string;
	fromDate?: DateOnly;
	toDate?: DateOnly;
	addRecipientsToTopic?: boolean;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{
			name: "EmailTopicName",
			value: getEmailTopicName(resource, defaultAreaId),
		},
		{ name: "EmailRecipients", value: emailRecipients },
		{ name: "EventsPublished", value: eventsPublished },
		{ name: "AddRecipientsToTopic", value: addRecipientsToTopic },
	];

	const trimmedNote = publisherNote?.trim();
	if (trimmedNote) {
		parameters.push({ name: "PublisherNote", value: trimmedNote });
	}
	if (fromDate !== undefined) {
		parameters.push({ name: "FromDate", value: fromDate });
	}
	if (toDate !== undefined) {
		parameters.push({ name: "ToDate", value: toDate });
	}

	const resp = await apiService.callV2(
		`${Number(defaultAreaId) === MILL_1.id ? "[M1].[SendCalendarPublishNotification]" : "[M2].[SendCalendarPublishNotification]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error sending publish notification");
	}
};
